/** @jsx jsx */
import { Global, css, jsx } from '@emotion/react';

import { useEffect } from 'react';

import cn from 'classnames';

import { breakpoints } from '@coursera/cds-core';

import Conversation from 'bundles/ai-course-coach/components/Conversation/Conversation';
import type { AnyFunction, HomeView, SendMessageFunc } from 'bundles/ai-course-coach/utils/types';

import _t from 'i18n!nls/learning-assistant-chat-ai';

const styles = {
  globalDesktop:
    // overrides the smart scroll container zIndex value when AI coach is in full screen mode in desktop screen
    css`
      .hide-smart-scroll-container,
      .show-smart-scroll-container,
      .SmartScrollWrapper {
        z-index: -1 !important;
      }
    `,
  // overrides the smart scroll container zIndex value when AI coach is in full screen mode in mobile screen
  globalMobile: css`
    ${breakpoints.down('xs')} {
      .ai-course-coach-widget {
        overflow: hidden;
      }

      .show-smart-scroll-container {
        z-index: -1 !important;
      }
    }
  `,

  conversationContainer: css`
    height: 100%;
    border-radius: var(--cds-border-radius-200);
    box-shadow: 0 4px 40px -8px rgb(0 0 0 / 40%), 0 4px 8px -8px rgb(0 0 0 / 40%);
    overflow: hidden;
  `,
};

export type ConversationContainerProps = {
  senderPlaceHolder: string;
  showCloseButton: boolean;
  showFullScreenButton: boolean;
  isFullScreen: boolean;
  disabledInput: boolean;
  autofocus: boolean;
  className: string;
  sendMessage: SendMessageFunc;
  toggleChat: AnyFunction;
  toggleFullScreen: () => void;
  profileClientAvatar?: string;
  onTextInputChange?: (text: string) => void;
  handleNewSession?: () => void;
  homeView?: HomeView;
  loading?: boolean;
  courseId?: string;
};

/**
 * Wrapper on `<Conversation />` for the "chat widget" mode of Coach conversations.
 */
function ConversationContainer({
  senderPlaceHolder,
  showCloseButton,
  showFullScreenButton,
  isFullScreen,
  disabledInput,
  autofocus,
  className,
  sendMessage,
  toggleChat,
  toggleFullScreen,
  profileClientAvatar,
  onTextInputChange,
  handleNewSession,
  homeView,
  loading,
  courseId,
}: ConversationContainerProps) {
  // this is a hook to maintain the animation state seperately than redux state, it prevents component re-rendering
  // this is for an animation quirk, generally state should be maintained in one location. to mitigate this, we pass
  // the redux state of isFullScreen into the hook... so any time it changes, we would update appropriately.

  const handleFullScreenToggle = () => {
    toggleFullScreen();
  };

  useEffect(() => {
    if (document.body) {
      document.body.classList.add('ai-course-coach-widget');
    }
    return () => {
      if (document.body) {
        document.body.classList.remove('ai-course-coach-widget');
      }
    };
  }, []);

  return (
    <div
      role="region"
      aria-label={_t('Coach chat window')}
      className={cn('coach-conversation-container', className)}
      css={[styles.conversationContainer]}
    >
      <Global styles={isFullScreen ? [styles.globalDesktop] : styles.globalMobile} />
      <Conversation
        autofocus={autofocus}
        isFullScreen={isFullScreen}
        senderPlaceHolder={senderPlaceHolder}
        showCloseButton={showCloseButton}
        showFullScreenButton={showFullScreenButton}
        disabledInput={disabledInput}
        sendMessage={sendMessage}
        toggleChat={toggleChat}
        profileClientAvatar={profileClientAvatar}
        onTextInputChange={onTextInputChange}
        handleNewSession={handleNewSession}
        homeView={homeView}
        loading={loading}
        courseId={courseId}
        toggleFullScreen={handleFullScreenToggle}
      />
    </div>
  );
}

export default ConversationContainer;
